import React, { useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { LanguageProvider } from '@/contexts/LanguageContext';

// Components
import PWAInstallPrompt from '@/components/PWAInstallPrompt';
import SecurityProtection from '@/components/SecurityProtection';
import { MarketplaceAudienceTracker } from '@/components/marketplace/MarketplaceAudienceTracker';

// Auth pages (kept for Marketplace admin login / password recovery)
import LoginPage from '@/pages/LoginPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';

// Marketplace pages
import MarketplacePage from '@/pages/MarketplacePage';
import MarketplaceProlongarPage from '@/pages/MarketplaceProlongarPage';
import MarketplaceAdminPage from '@/pages/MarketplaceAdminPage';
import MarketplacePagamentoSucesso from '@/pages/MarketplacePagamentoSucesso';
import MarketplacePagamentoCancelado from '@/pages/MarketplacePagamentoCancelado';
import MarketplaceListingPage from '@/pages/MarketplaceListingPage';
import MarketplaceCityPage from '@/pages/MarketplaceCityPage';
import MarketplaceBuyPage from '@/pages/MarketplaceBuyPage';
import MarketplaceSellPage from '@/pages/MarketplaceSellPage';
import MarketplaceAlertManagePage from '@/pages/MarketplaceAlertManagePage';
import MarketplaceSaleDetailPage from '@/pages/MarketplaceSaleDetailPage';
import MarketplacePricingPage from '@/pages/MarketplacePricingPage';
import MarketplaceConversationsPage from '@/pages/MarketplaceConversationsPage';
import MarketplacePartnersPage from '@/pages/MarketplacePartnersPage';
import MarketplacePartnerDetailPage from '@/pages/MarketplacePartnerDetailPage';
import MarketplacePartnerFormPage from '@/pages/MarketplacePartnerFormPage';
import MarketplaceConversationAccessPage from '@/pages/MarketplaceConversationAccessPage';
import MarketplaceAdvertisingPage from '@/pages/MarketplaceAdvertisingPage';
import { DISTRICT_MAP } from '@/lib/districts';
import { LivreResolucaoPage, SegurancaPage } from '@/pages/MarketplaceExtraPages';
import NewsCenterPage from '@/pages/NewsCenterPage';
import NewsArticlePage from '@/pages/NewsArticlePage';
import NewsCategoryPage from '@/pages/NewsCategoryPage';
import NewsLegislationPage from '@/pages/NewsLegislationPage';
import {
  TermosCondicoes,
  PoliticaPrivacidade,
  PoliticaCookies,
  AvisoLegal,
} from '@/pages/MarketplaceLegalPages';

// Register Service Worker
const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
      });
      console.log('Service Worker registered:', registration.scope);
    } catch (error) {
      console.error('Service Worker registration failed:', error);
    }
  }
};

// Protected Route Component (Marketplace admin only)
const ProtectedRoute = ({ children }) => {
  const { user, loading, sessionExpired } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
      </div>
    );
  }

  // Se sessão expirou mas temos dados do user em cache, mostrar a página com o banner
  if (!user && !sessionExpired) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Apenas admins do marketplace (ou super admin) podem entrar na área admin
  if (user && !user.is_marketplace_admin && !user.is_super_admin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Resolve /viaturas/:slugId e /motoristas/:slugId: distrito (página SEO) vs anúncio individual.
// Os slugs de anúncio terminam sempre com "-xxxxxxxx" (8 hex do id).
const LISTING_SLUG_RE = /-[a-f0-9]{8}$/i;
function ViaturasSlugResolver() {
  const { slugId } = useParams();
  if (!LISTING_SLUG_RE.test(slugId) && DISTRICT_MAP[slugId]) {
    return <MarketplaceCityPage tipo="viatura" />;
  }
  return <MarketplaceListingPage />;
}
function MotoristasSlugResolver() {
  const { slugId } = useParams();
  if (!LISTING_SLUG_RE.test(slugId) && DISTRICT_MAP[slugId]) {
    return <MarketplaceCityPage tipo="motorista" />;
  }
  return <MarketplaceListingPage />;
}

function AnunciarViaturaRoute() {
  const [params] = useSearchParams();
  const wantsSale = params.get('finalidade') === 'venda' || params.get('venda') === '1'
    || params.get('tipo') === 'venda';
  if (wantsSale) return <Navigate to="/vender-viatura" replace />;
  return <MarketplacePage initialAction="viatura" />;
}

function AppContent() {
  return (
    <Routes>
      {/* Marketplace público (listagens, pesquisa, perfis de parceiros) */}
      <Route path="/" element={<MarketplacePage />} />
      <Route path="/anunciar-viatura" element={<AnunciarViaturaRoute />} />
      <Route path="/publicar-procura" element={<MarketplacePage initialAction="motorista" />} />
      <Route path="/gerir-anuncio" element={<MarketplacePage initialAction="manage" />} />
      {/* Área separada de compra e venda de viaturas (Fase 3) */}
      <Route path="/comprar-viaturas-tvde" element={<MarketplaceBuyPage />} />
      <Route path="/comprar-viaturas-tvde/:id" element={<MarketplaceSaleDetailPage />} />
      <Route path="/vender-viatura" element={<MarketplaceSellPage />} />
      <Route path="/alertas/gerir" element={<MarketplaceAlertManagePage />} />
      <Route path="/anunciar-venda" element={<Navigate to="/vender-viatura" replace />} />
      <Route path="/viaturas-venda" element={<Navigate to="/comprar-viaturas-tvde" replace />} />
      {/* Páginas SEO por categoria e distrito */}
      <Route path="/viaturas" element={<MarketplaceCityPage tipo="viatura" />} />
      <Route path="/motoristas" element={<MarketplaceCityPage tipo="motorista" />} />
      <Route path="/precos" element={<MarketplacePricingPage />} />
      <Route path="/publicidade" element={<MarketplaceAdvertisingPage />} />
      <Route path="/conversas" element={<MarketplaceConversationsPage />} />
      <Route path="/parceiros" element={<MarketplacePartnersPage preset="parceiros" />} />
      <Route path="/stands" element={<MarketplacePartnersPage preset="stands" />} />
      <Route path="/associacoes-tvde" element={<MarketplacePartnersPage preset="associacoes" />} />
      <Route path="/profissionais-tvde" element={<MarketplacePartnersPage preset="profissionais" />} />
      <Route path="/adicionar-parceiro" element={<MarketplacePartnerFormPage />} />
      <Route path="/parceiros/:slug" element={<MarketplacePartnerDetailPage />} />
      <Route path="/conversas/acesso" element={<MarketplaceConversationAccessPage />} />
      <Route path="/conversas/:id" element={<MarketplaceConversationsPage />} />
      <Route path="/viaturas/:slugId" element={<ViaturasSlugResolver />} />
      <Route path="/motoristas/:slugId" element={<MotoristasSlugResolver />} />
      <Route path="/noticias-tvde" element={<NewsCenterPage />} />
      <Route path="/noticias-tvde/legislacao" element={<NewsLegislationPage />} />
      <Route path="/noticias-tvde/artigo/:slug" element={<NewsArticlePage />} />
      <Route path="/noticias-tvde/categoria/:slug" element={<NewsCategoryPage />} />
      <Route path="/marketplace" element={<Navigate to="/" replace />} />

      {/* Autenticação — login do admin do marketplace + recuperação de password */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Pagamento de anúncios (Stripe) */}
      <Route path="/marketplace/pagamento/sucesso" element={<MarketplacePagamentoSucesso />} />
      <Route path="/marketplace/pagamento/cancelado" element={<MarketplacePagamentoCancelado />} />

      {/* Prolongar anúncio — magic link enviado por email */}
      <Route path="/marketplace/prolongar/:token" element={<MarketplaceProlongarPage />} />

      {/* Área de administração do Marketplace */}
      <Route path="/marketplace-admin/audiencia" element={<ProtectedRoute><MarketplaceAdminPage initialSection="audiencia" /></ProtectedRoute>} />
      <Route path="/marketplace-admin/noticias/preview/:slug" element={<ProtectedRoute><NewsArticlePage preview /></ProtectedRoute>} />
      <Route path="/marketplace-admin/noticias/legislacao" element={<ProtectedRoute><NewsLegislationPage preview /></ProtectedRoute>} />
      <Route
        path="/marketplace-admin"
        element={
          <ProtectedRoute>
            <MarketplaceAdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/marketplace"
        element={<Navigate to="/marketplace-admin" replace />}
      />

      {/* Páginas legais (RGPD / Termos / Cookies / Aviso) */}
      <Route path="/termos" element={<TermosCondicoes />} />
      <Route path="/privacidade" element={<PoliticaPrivacidade />} />
      <Route path="/cookies" element={<PoliticaCookies />} />
      <Route path="/aviso-legal" element={<AvisoLegal />} />
      <Route path="/livre-resolucao" element={<LivreResolucaoPage />} />
      <Route path="/seguranca" element={<SegurancaPage />} />

      {/* Qualquer outra rota volta ao Marketplace */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

const SkipToContent = () => (
  <a
    href="#conteudo-principal"
    className="skip-to-content"
    data-testid="skip-to-content"
    onClick={(e) => {
      const main = document.querySelector('main') || document.getElementById('conteudo-principal');
      if (main) {
        e.preventDefault();
        main.setAttribute('tabindex', '-1');
        main.focus();
        main.scrollIntoView();
      }
    }}
  >
    Saltar para o conteúdo principal
  </a>
);

function App() {
  // Register Service Worker on mount
  useEffect(() => {
    registerServiceWorker();
  }, []);

  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <SecurityProtection>
            <MarketplaceAudienceTracker />
            <SkipToContent />
            <AppContent />
            <PWAInstallPrompt />
          </SecurityProtection>
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
